import { BrowserRouter, Routes, Route, Navigate, Outlet } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { ThemeProvider } from "@/context/ThemeContext";
import { Toaster } from "@/components/ui/sonner";
import AppLayout from "@/components/layout/AppLayout";
import Login from "@/pages/Login";
import CommandCenter from "@/pages/CommandCenter";
import ExecutiveDashboard from "@/pages/ExecutiveDashboard";
import Candidates from "@/pages/Candidates";
import CandidateDetail from "@/pages/CandidateDetail";
import Jobs from "@/pages/Jobs";
import JobDetail from "@/pages/JobDetail";
import Submissions from "@/pages/Submissions";
import Placements from "@/pages/Placements";
import Offers from "@/pages/Offers";
import Clients from "@/pages/Clients";
import ClientDetail from "@/pages/ClientDetail";
import Interviews from "@/pages/Interviews";
import Contractors from "@/pages/Contractors";
import Timesheets from "@/pages/Timesheets";
import BDMCommandCenter from "@/pages/BDMCommandCenter";
import SalesPipeline from "@/pages/SalesPipeline";
import RADashboard from "@/pages/ra/RADashboard";
import RATitles from "@/pages/ra/RATitles";
import RALeads from "@/pages/ra/RALeads";
import RAReview from "@/pages/ra/RAReview";
import RATargets from "@/pages/ra/RATargets";
import RAPerformance from "@/pages/ra/RAPerformance";
import RAConfig from "@/pages/ra/RAConfig";
import RADuplicateCheck from "@/pages/ra/RADuplicateCheck";
import RASyncCenter from "@/pages/ra/RASyncCenter";
import RACatalog from "@/pages/ra/RACatalog";
import RADispatchBoard from "@/pages/ra/RADispatchBoard";
import ExportsCenter from "@/pages/ra/ExportsCenter";
import RATeams from "@/pages/ra/RATeams";
import ModulePage from "@/pages/ModulePage";
import Pipeline from "@/pages/Pipeline";
import Reports from "@/pages/Reports";
import AIIntelligence from "@/pages/AIIntelligence";
import Admin from "@/pages/Admin";
import TeamAccess from "@/pages/TeamAccess";
import AcceptInvitation from "@/pages/AcceptInvitation";
import HRMS from "@/pages/HRMS";
import EmployeeProfile from "@/pages/EmployeeProfile";
import AttendanceManagement from "@/pages/AttendanceManagement";
import LeaveManagement from "@/pages/LeaveManagement";
import PayrollWorkspace from "@/pages/PayrollWorkspace";
import ComingSoon from "@/pages/ComingSoon";
import Communications from "@/pages/Communications";
import NexreqAI from "@/pages/NexreqAI";
import ChatGPTWorkspace from "@/pages/ChatGPTWorkspace";
import Invoices from "@/pages/Invoices";
import MyTasks from "@/pages/MyTasks";
import NexreqDrive from "@/pages/NexreqDrive";
import NotificationSettings from "@/pages/NotificationSettings";
import NotificationCenter from "@/pages/NotificationCenter";
import DesktopNotificationBridge from "@/components/DesktopNotificationBridge";
import { IdleTimeoutGuard } from "@/components/layout/IdleTimeoutGuard";
import { Loader2 } from "lucide-react";

function SessionLoader() {
  const { offline } = useAuth();
  return (
    <div className="min-h-screen grid place-items-center bg-background" data-testid="session-loader">
      <div className="flex flex-col items-center gap-3 text-sm text-muted-foreground">
        <Loader2 className="h-6 w-6 animate-spin text-[#255058]" />
        {offline ? "Connection lost — reconnecting…" : "Restoring your session…"}
      </div>
    </div>
  );
}

function Protected({ children }) {
  const { user } = useAuth();
  if (user === null) return <SessionLoader />;
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function Shell() {
  const { user } = useAuth();
  if (user === null) return <SessionLoader />;
  if (!user) return <Navigate to="/login" replace />;
  return (
    <>
      <IdleTimeoutGuard />
      <AppLayout />
    </>
  );
}

function Home() {
  const { user } = useAuth();
  if (["super_admin", "founder"].includes(user?.role)) return <ExecutiveDashboard />;
  if (user?.role === "research_analyst") return <Navigate to="/ra" replace />;
  return <CommandCenter />;
}

function FounderRATaskGate() {
  const { user } = useAuth();
  if (user?.role === "founder") return <Navigate to="/" replace />;
  return <Outlet />;
}

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <BrowserRouter>
          <DesktopNotificationBridge />
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/invite" element={<AcceptInvitation />} />
            <Route path="/" element={<Shell />}>
              <Route index element={<Home />} />
              <Route path="candidates" element={<Candidates />} />
              <Route path="candidates/:id" element={<CandidateDetail />} />
              <Route path="clients" element={<Clients />} />
              <Route path="clients/:id" element={<ClientDetail />} />
              <Route path="contacts" element={<ModulePage moduleKey="contacts" />} />
              <Route path="sales" element={<SalesPipeline />} />
              <Route path="bdm" element={<BDMCommandCenter />} />
              <Route element={<FounderRATaskGate />}>
                <Route path="ra" element={<RADashboard />} />
                <Route path="ra/titles" element={<RATitles />} />
                <Route path="ra/assign-titles" element={<RATitles adminMode />} />
                <Route path="ra/leads" element={<RALeads />} />
                <Route path="ra/duplicate-check" element={<RADuplicateCheck />} />
                <Route path="ra/sync" element={<RASyncCenter />} />
                <Route path="ra/outreach" element={<RALeads preset="outreach" />} />
                <Route path="ra/replies" element={<RALeads preset="replies" />} />
                <Route path="ra/submitted" element={<RALeads preset="submitted" />} />
                <Route path="ra/targets" element={<RATargets />} />
                <Route path="ra/performance" element={<RAPerformance />} />
                <Route path="ra/review" element={<RAReview />} />
                <Route path="ra/config" element={<RAConfig />} />
                <Route path="ra/catalog" element={<RACatalog />} />
                <Route path="ra/teams" element={<RATeams />} />
                <Route path="ra/dispatch" element={<RADispatchBoard />} />
                <Route path="exports" element={<ExportsCenter />} />
              </Route>
              <Route path="ra/team" element={<RAPerformance team />} />
              <Route path="jobs" element={<Jobs />} />
              <Route path="jobs/:id" element={<JobDetail />} />
              <Route path="pipeline" element={<Pipeline />} />
              <Route path="submissions" element={<Submissions />} />
              <Route path="interviews" element={<Interviews />} />
              <Route path="offers" element={<Offers />} />
              <Route path="placements" element={<Placements />} />
              <Route path="contractors" element={<Contractors />} />
              <Route path="timesheets" element={<Timesheets />} />
              <Route path="tasks" element={<MyTasks />} />
              <Route path="my-tasks" element={<MyTasks />} />
              <Route path="calendar" element={<ComingSoon title="Calendar" phase="Phase 7" />} />
              <Route path="communications" element={<Communications />} />
              <Route path="nexreq-ai" element={<NexreqAI />} />
              <Route path="chatgpt" element={<ChatGPTWorkspace />} />
              <Route path="revenue" element={<Invoices />} />
              <Route path="invoices" element={<Invoices />} />
              <Route path="reports" element={<Reports />} />
              <Route path="ai" element={<AIIntelligence />} />
              <Route path="hrms" element={<HRMS />} />
              <Route path="hrms/employees/:id" element={<EmployeeProfile />} />
              <Route path="hrms/attendance" element={<AttendanceManagement />} />
              <Route path="hrms/leave-management" element={<LeaveManagement />} />
              <Route path="hrms/payroll" element={<PayrollWorkspace />} />
              <Route
                path="automations"
                element={<ComingSoon title="Automations" phase="Phase 11" />}
              />
              <Route path="admin" element={<Admin />} />
              <Route path="team-access" element={<TeamAccess />} />
              <Route path="nexreq-drive" element={<NexreqDrive />} />
              <Route path="notification-settings" element={<NotificationSettings />} />
              <Route path="notifications" element={<NotificationCenter />} />
            </Route>
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
        <Toaster position="top-right" richColors />
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
